import { db } from '../firebase/config';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import type { LeaderboardEntry } from '../../domain/leaderboard/types';
import { summaryToLeaderboardEntry } from '../../domain/leaderboard/types';
import type { Workout } from '../../domain/workouts/types';
import type { ScoringSex } from '../../domain/scoring/types';
import { buildMonthlySummary, buildExerciseBests } from '../../domain/workouts/monthlyBests';
import { normalizeCategory } from '../../domain/scoring/categories';

async function fetchWithRetry<T>(fn: () => Promise<T>, retries = 2): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (retries > 0) {
      await new Promise((r) => setTimeout(r, 400));
      return fetchWithRetry(fn, retries - 1);
    }
    throw err;
  }
}

export async function getLeaderboardEntries(monthKey: string): Promise<LeaderboardEntry[]> {
  const workoutsRef = collection(db, 'workouts');
  const workoutsQuery = query(workoutsRef, where('monthKey', '==', monthKey));

  const [workoutsSnap, usersSnap] = await Promise.all([
    fetchWithRetry(() => getDocs(workoutsQuery)),
    fetchWithRetry(() => getDocs(collection(db, 'users'))),
  ]);

  const userNames: Record<string, string> = {};
  const userSexes: Record<string, ScoringSex> = {};
  usersSnap.forEach((doc) => {
    const data = doc.data();
    if (data.displayName) userNames[doc.id] = data.displayName;
    if (data.scoringSex === 'male' || data.scoringSex === 'female') {
      userSexes[doc.id] = data.scoringSex;
    }
  });

  const userWorkouts: Record<string, Workout[]> = {};
  workoutsSnap.forEach((doc) => {
    const raw = doc.data() as Workout;
    // Workouts written before the hinge rename store category 'pull'.
    const data: Workout = { ...raw, category: normalizeCategory(raw.category) };
    const userId = data.userId;
    if (!userWorkouts[userId]) {
      userWorkouts[userId] = [];
    }
    userWorkouts[userId].push(data);
  });

  const entries: LeaderboardEntry[] = [];

  for (const [userId, workouts] of Object.entries(userWorkouts)) {
    const summary = buildMonthlySummary(workouts);
    // Per-exercise bests come off the same workouts, so the per-lift boards cost
    // no extra reads.
    const exerciseBests = buildExerciseBests(workouts);
    entries.push({
      ...summaryToLeaderboardEntry(
        userId,
        userNames[userId] || 'Unknown User',
        monthKey,
        summary,
        exerciseBests,
        // Profile first; a workout's snapshot is the fallback if the profile is
        // missing the field, so nobody silently drops out of a gender filter.
        userSexes[userId] ?? workouts[0]?.scoringSexSnapshot ?? 'male',
      ),
    });
  }

  return entries;
}

/** A person, as the feed and the admin list need them. */
export interface FeedUser {
  displayName: string;
  scoringSex?: ScoringSex;
  /** Admin only, for telling two profiles with the same name apart. */
  normalizedEmail?: string;
}

/**
 * Every workout by everyone, newest first, plus the people who logged them.
 *
 * Deliberately unfiltered and unlimited: the feed's badges need each lifter's
 * full history to know what a lift beat, and the admin list is meant to show
 * everything. One `orderBy` on a single field needs no composite index. For a
 * friend group that is a small read; if the collection ever outgrows it,
 * precompute the record flags when a workout is written rather than narrowing
 * this query, because a partial history would produce quietly wrong badges.
 */
export async function getAllWorkoutsWithUsers(): Promise<{
  workouts: Workout[];
  users: Record<string, FeedUser>;
}> {
  const workoutsQuery = query(collection(db, 'workouts'), orderBy('workoutDate', 'desc'));

  const [workoutsSnap, usersSnap] = await Promise.all([
    fetchWithRetry(() => getDocs(workoutsQuery)),
    fetchWithRetry(() => getDocs(collection(db, 'users'))),
  ]);

  const users: Record<string, FeedUser> = {};
  usersSnap.forEach((doc) => {
    const data = doc.data();
    users[doc.id] = {
      displayName: data.displayName ?? 'Unknown User',
      scoringSex:
        data.scoringSex === 'male' || data.scoringSex === 'female' ? data.scoringSex : undefined,
      normalizedEmail: typeof data.normalizedEmail === 'string' ? data.normalizedEmail : undefined,
    };
  });

  const workouts: Workout[] = workoutsSnap.docs.map((doc) => {
    const raw = doc.data() as Workout;
    return {
      ...raw,
      id: doc.id,
      // Workouts written before the hinge rename store category 'pull'.
      category: normalizeCategory(raw.category),
      // Firestore hands back Timestamps; the feed sorts on this.
      createdAt: (raw.createdAt as unknown as { toDate?: () => Date })?.toDate?.() ?? new Date(0),
    };
  });

  return { workouts, users };
}

/** The feed only needs names, so it takes the same read and drops the rest. */
export async function getFeedData(): Promise<{
  workouts: Workout[];
  userNames: Record<string, string>;
}> {
  const { workouts, users } = await getAllWorkoutsWithUsers();
  const userNames = Object.fromEntries(Object.entries(users).map(([id, u]) => [id, u.displayName]));
  return { workouts, userNames };
}
